/** @format */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal, Pagination, SegmentedControl, Skeleton } from "@mantine/core";
import { BellOff } from "tabler-icons-react";
import { getValueForInput, loadingStates } from "../utilities/utilities";
import { apiWrapWithErrorWithData } from "../utilities/apiHelpers";
import {
  getSelfNotifications,
  updateNotificationStatus,
} from "../utilities/apis/users";
import TaskDetailsUI from "../components/TaskNewUI/TaskDetailsUI";
import DashBoardNotificationBox from "../components/CaseNotification/DashBoardNotificationBox";
import colors from "../utilities/design";

const PAGE_SIZE = 10;

const FILTER_TABS = [
  { label: "All", value: "all" },
  { label: "Agreements", value: "agreements" },
  { label: "Tasks", value: "tasks" },
];

const buildNotificationFilters = (filterKey) => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const filters = {
    sentAt: { gte: thirtyDaysAgo.toISOString() },
  };

  const key = filterKey?.toLowerCase();
  if (key === "agreements") filters.agreementId = { not: null };
  if (key === "tasks") filters.taskId = { not: null };
  if (key === "cases") filters.caseId = { not: null };
  if (key === "notices") filters.noticeId = { not: null };

  return filters;
};

const getAgreementId = (notif) =>
  notif.agreement?.id ||
  (notif.parent === "agreement" && notif.parentId ? notif.parentId : null);

const canOpenNotification = (notif) =>
  Boolean(
    notif.task || notif.case || notif.notice || getAgreementId(notif),
  );

const getNotificationActionLabel = (notif) => {
  if (notif.task) return "View Task";
  if (notif.case) return "View Case";
  if (getAgreementId(notif)) return "View Agreement";
  if (notif.notice) return "View Notice";
  return "View";
};

const NotificationsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [filterOptions, setFilterOptions] = useState(
    location?.state?.filterOptions || "all",
  );
  const [loading, setLoading] = useState(loadingStates.NO_ACTIVE_REQUEST);
  const [notifications, setNotifications] = useState([]);
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [taskToLoad, setTaskToLoad] = useState(null);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(notificationsCount / PAGE_SIZE)),
    [notificationsCount],
  );

  const fetchNotifications = useCallback(async () => {
    setLoading(loadingStates.LOADING);

    const resp = await apiWrapWithErrorWithData(
      getSelfNotifications({
        page,
        filterOptions: buildNotificationFilters(filterOptions),
      }),
    );

    if (resp?.success && resp?.notifications) {
      setNotifications(resp.notifications);
      setNotificationsCount(resp.notificationsCount ?? 0);
    } else {
      setNotifications([]);
      setNotificationsCount(0);
    }

    setLoading(loadingStates.NO_ACTIVE_REQUEST);
  }, [filterOptions, page]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    setPage(1);
  }, [filterOptions]);

  const notificationClickHandler = async (notif) => {
    if (!notif.seen) {
      await apiWrapWithErrorWithData(
        updateNotificationStatus({ id: notif.id }),
      );
    }

    if (notif.task) {
      setTaskToLoad({
        parent: notif.parent,
        parentId: notif.parentId,
        parentTitle: notif.parentTitle,
        id: notif.task.id,
      });
      return;
    }

    if (notif.case) {
      navigate(`/app/dispute-manager/cases/details/${notif.case.id}`);
      return;
    }

    if (notif.notice) {
      navigate(`/app/dispute-manager/legal-notices/details/${notif.notice.id}`);
      return;
    }

    const agreementId = getAgreementId(notif);
    if (agreementId) {
      navigate(`/app/agreements_manager/agreements/details/${agreementId}`);
    }
  };

  const isLoading = loading === loadingStates.LOADING;

  return (
    <>
      {taskToLoad && (
        <Modal
          overflow="inside"
          opened
          onClose={() => setTaskToLoad(null)}
          size="calc(80vw)"
        >
          <TaskDetailsUI
            parent={taskToLoad.parent}
            parentId={taskToLoad.parentId}
            taskId={taskToLoad.id}
            title={taskToLoad.parentTitle}
            onModalExit={() => setTaskToLoad(null)}
          />
        </Modal>
      )}

      <div className="w-full">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-[Lexend,sans-serif] text-lg font-semibold text-slate-900">
            Notifications
          </h1>
          <SegmentedControl
            value={filterOptions}
            onChange={(value) => setFilterOptions(getValueForInput(value))}
            data={FILTER_TABS}
            color="cyan"
            size="sm"
            styles={{
              root: { backgroundColor: "#f1f5f9" },
              label: { fontWeight: 600, textTransform: "capitalize" },
            }}
          />
        </div>

        {isLoading && (
          <div className="flex flex-col gap-1.5">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} height={42} radius="sm" />
            ))}
          </div>
        )}

        {!isLoading && notifications.length === 0 && (
          <div className="flex flex-col items-center gap-1 py-6">
            <BellOff size={32} strokeWidth={1.5} color="#94a3b8" />
            <p className="text-sm font-semibold text-slate-700">
              No notifications
            </p>
          </div>
        )}

        {!isLoading && notifications.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="text-xs text-slate-500">
              {notificationsCount} total · page {page}/{totalPages}
            </p>

            {notifications.map((notif) => (
              <DashBoardNotificationBox
                key={notif.id}
                backgroundColor={colors[notif.module?.toLowerCase()]}
                date={notif.sentAt}
                seen={notif.seen}
                module={notif.module?.toUpperCase()}
                notificationText={notif.notificationText}
                btnName={
                  canOpenNotification(notif)
                    ? getNotificationActionLabel(notif)
                    : ""
                }
                onBtnClick={
                  canOpenNotification(notif)
                    ? () => notificationClickHandler(notif)
                    : undefined
                }
                isDeleted={
                  notif.agreement?.isDeleted ||
                  notif.case?.isDeleted ||
                  notif.notice?.isDeleted ||
                  notif.task?.isDeleted
                }
              />
            ))}

            {notificationsCount > PAGE_SIZE && (
              <div className="mt-2 flex justify-center">
                <Pagination
                  page={page}
                  onChange={setPage}
                  total={totalPages}
                  color="cyan"
                  radius="md"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationsPage;
